<template>
  <v-dialog v-model="open" max-width="600px" fullscreen-on-mobile>
    <v-card :loading="loading">
      <v-card-title>Return Item</v-card-title>
      <v-card-text>
        <v-form ref="returnForm" v-model="returnFormValid">
          <v-text-field
            v-model.number="returnForm.quantity"
            :label="
              selectedItem
                ? `Quantity Returned (Remaining: ${selectedItem.remainingQuantity})`
                : 'Quantity Returned'
            "
            type="number"
            min="1"
            :max="selectedItem ? selectedItem.remainingQuantity : 999"
            :rules="returnQuantityRules"
          ></v-text-field>

          <v-menu
            v-model="returnDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="returnForm.dateReturned"
                label="Return Date"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="returnForm.dateReturned"
              @input="returnDateMenu = false"
            ></v-date-picker>
          </v-menu>

          <v-textarea
            v-model="returnForm.notes"
            label="Notes (optional)"
            rows="3"
            auto-grow
            counter="512"
            maxlength="512"
            :rules="notesRules"
          ></v-textarea>

          <v-checkbox
            v-model="returnForm.returnToInventory"
            label="Add returned items back to inventory"
          ></v-checkbox>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="open = false" :disabled="loading">Cancel</v-btn>
        <v-btn
          color="primary"
          text
          @click="returnItem"
          :disabled="!returnForm.quantity || !returnFormValid || loading"
          :loading="loading"
          >Mark as Returned</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from "vue";
import moment from "moment-timezone";
import { mapState } from "vuex";

export default {
  name: "ReturnItemDialog",

  data: () => ({
    open: false,
    loading: false,
    selectedItem: null,
    driverId: null,

    returnFormValid: false,
    returnDateMenu: false,

    returnForm: {
      dateReturned: "",
      quantity: null,
      returnToInventory: false,
      notes: "",
    },
  }),

  computed: {
    ...mapState("companyModule", ["company"]),

    returnQuantityRules() {
      return [
        (v) => !!v || "Quantity is required",
        (v) => v > 0 || "Quantity must be greater than 0",
        (v) => Number.isInteger(Number(v)) || "Quantity must be a whole number",
        (v) => {
          if (this.selectedItem && v > this.selectedItem.remainingQuantity) {
            return `Cannot return more than ${this.selectedItem.remainingQuantity} items`;
          }
          return true;
        },
      ];
    },

    notesRules() {
      return [
        (v) => !v || v.length <= 512 || "Notes cannot exceed 512 characters",
      ];
    },
  },

  methods: {
    getTodayDate() {
      return moment().tz(this.company.timeZone).format("YYYY-MM-DD");
    },

    openDialog(item, driverId) {
      this.selectedItem = item;
      this.driverId = driverId;
      this.loading = false;
      this.returnForm = {
        dateReturned: this.getTodayDate(),
        quantity: null,
        returnToInventory: false,
        notes: "",
      };
      this.open = true;
      this.$nextTick(() => {
        if (this.$refs.returnForm) {
          this.$refs.returnForm.resetValidation();
        }
      });
    },

    async returnItem() {
      if (!this.$refs.returnForm.validate()) {
        return;
      }

      this.loading = true;
      try {
        await this.$store.dispatch("assignmentModule/returnAssignment", {
          assignmentId: this.selectedItem.assignmentId,
          driverId: this.driverId,
          dateReturned: moment.tz(this.returnForm.dateReturned, this.company.timeZone).utc().format(),
          quantity: this.returnForm.quantity,
          returnToInventory: this.returnForm.returnToInventory,
          notes: this.returnForm.notes || null,
        });

        Vue.toasted.success("Item returned successfully", { duration: 3000 });
        this.open = false;
      } catch (error) {
        Vue.toasted.error(error, { duration: 5000 });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Hide spin buttons for number input */
::v-deep input[type="number"]::-webkit-outer-spin-button,
::v-deep input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

::v-deep input[type="number"] {
  -moz-appearance: textfield;
}
</style>
